import JSZip from "jszip";
import * as XLSX from "xlsx";

const TURKISH_MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık"
];

const WORKED_LIKE_CODES = new Set(["RT", "BT", "Yİ", "İD.İ", "SI", "Sİ", "ÖL.İ", "EV.İ", "YG"]);
const BREAKING_CODES = new Set(["R", "İ"]);
const SENSITIVE_MONTH_CODES = new Set(["R", "İ"]);
const MONTHLY_5510_TRANSFER_CODES = new Set(["RT", "BT"]);
const FULL_DAY_WEEKLY_CREDIT_CODES = new Set(["RT", "BT", "İd.İ", "Öl.İ", "Ev.İ"]);
const CONDITIONAL_FULL_DAY_WEEKLY_CREDIT_CODES = new Set(["Yİ"]);
const HALF_DAY_WEEKLY_CREDIT_CODES = new Set(["YG"]);

export const REPORT_CODE_DESCRIPTIONS = [
  ["RT", "Resmi Tatil"],
  ["BT", "Bayram Tatili"],
  ["İ", "1 yılı tamamlamamış yıllık izin"],
  ["Yİ", "Yıllık İzin"],
  ["İd.İ", "İdari İzin"],
  ["YG", "Yarım Gün İzin"],
  ["R", "Rapor"],
  ["Si", "Süt İzni"],
  ["Öl.İ", "Ölüm İzni"],
  ["Ev.İ", "Evlilik İzni"]
];

const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, " ");

export const getExemptionEmployeeKey = (personName = "") => normalizeText(personName);

const normalizeHeader = (value = "") =>
  normalizeText(value)
    .replace(/[ıİ]/g, "I")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]+/g, "");

const normalizeCode = (value = "") => {
  const text = String(value || "").trim();
  const upper = text.toLocaleUpperCase("tr-TR").replace(/\s+/g, "");

  if (upper === "ID.I" || upper === "İD.İ" || upper === "İDİ" || upper === "IDI") return "İd.İ";
  if (upper === "SI" || upper === "Sİ") return "Si";
  if (upper === "ÖL.İ" || upper === "ÖLİ" || upper === "OL.I" || upper === "OLI") return "Öl.İ";
  if (upper === "EV.İ" || upper === "EVİ" || upper === "EV.I" || upper === "EVI") return "Ev.İ";
  if (upper === "Yİ" || upper === "YI") return "Yİ";
  if (upper === "YG") return "YG";
  if (upper === "İ" || upper === "I") return "İ";

  return upper;
};

export const getPeriodInfo = (periodKey) => {
  const [yearValue, monthValue] = String(periodKey || "").split("-").map(Number);
  const year = yearValue || new Date().getFullYear();
  const month = monthValue || new Date().getMonth() + 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthName = TURKISH_MONTHS[month - 1] || "";
  const monthUpper = monthName.toLocaleUpperCase("tr-TR");

  return {
    year,
    month,
    monthNumberLabel: String(month).padStart(2, "0"),
    monthName,
    monthUpper,
    daysInMonth,
    label: `${monthName} ${year}`,
    labelUpper: `${monthUpper} ${year}`
  };
};

export const parseDayList = (text = "") =>
  String(text || "")
    .split(/[,\n;\s]+/)
    .map((item) => Number(String(item).trim()))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 31);

const parseDayExpression = (text = "") => {
  const days = new Set();
  const tokens = String(text || "")
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  tokens.forEach((token) => {
    const rangeParts = token
      .split("-")
      .map((part) => Number(String(part).trim()))
      .filter((day) => Number.isInteger(day));

    if (rangeParts.length === 1) {
      const [day] = rangeParts;

      if (day >= 1 && day <= 31) {
        days.add(day);
      }

      return;
    }

    if (rangeParts.length >= 2) {
      const startDay = rangeParts[0];
      const endDay = rangeParts[rangeParts.length - 1];

      if (startDay >= 1 && endDay <= 31 && startDay <= endDay) {
        for (let day = startDay; day <= endDay; day += 1) {
          days.add(day);
        }

        return;
      }

      rangeParts.forEach((day) => {
        if (day >= 1 && day <= 31) {
          days.add(day);
        }
      });
    }
  });

  return Array.from(days).sort((a, b) => a - b);
};

export const parseSpecialCodeLines = (text = "") => {
  const map = new Map();
  const warnings = [];

  String(text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line, index) => {
      const parts = line.split(";").map((part) => part.trim());

      if (parts.length < 3) {
        warnings.push(`${index + 1}. satır okunamadı: ${line}`);
        return;
      }

      const [personName, dayText, codeText] = parts;
      const days = parseDayExpression(dayText);
      const code = normalizeCode(codeText);

      if (!personName || days.length === 0 || !code) {
        warnings.push(`${index + 1}. satır eksik/hatalı: ${line}`);
        return;
      }

      days.forEach((day) => {
        map.set(`${normalizeText(personName)}::${day}`, code);
      });
    });

  return { map, warnings };
};

const findHeaderRowIndex = (rows) =>
  rows.findIndex((row) =>
    row.some((cell) => normalizeHeader(cell) === "PERSONEL")
  );

const findColumnIndex = (headerRow, possibleHeaders) => {
  const normalizedPossibleHeaders = possibleHeaders.map(normalizeHeader);
  return headerRow.findIndex((cell) => normalizedPossibleHeaders.includes(normalizeHeader(cell)));
};

const getCell = (row, index) => (index >= 0 ? row[index] : "");

const getWorksheetCell = (worksheet, rowIndex, columnIndex) => {
  if (!worksheet || rowIndex < 0 || columnIndex < 0) return null;

  return worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })] || null;
};

const getCellFillRgb = (cell) => {
  const rawRgb =
    cell?.s?.fgColor?.rgb ||
    cell?.s?.fill?.fgColor?.rgb ||
    cell?.s?.bgColor?.rgb ||
    "";

  return String(rawRgb || "")
    .replace(/^FF/i, "")
    .replace(/[^0-9A-F]/gi, "")
    .toLocaleUpperCase("tr-TR");
};

const isAnnualLeaveGreyFill = (cell) => {
  const rgb = getCellFillRgb(cell);

  if (rgb.length !== 6) return false;

  const red = parseInt(rgb.slice(0, 2), 16);
  const green = parseInt(rgb.slice(2, 4), 16);
  const blue = parseInt(rgb.slice(4, 6), 16);
  const channelsAreGrey = Math.max(red, green, blue) - Math.min(red, green, blue) <= 8;
  const isMediumGrey = red >= 120 && red <= 220;

  return channelsAreGrey && isMediumGrey;
};

const getWeekNumberFromTitle = (value = "") => {
  const match = String(value || "").match(/(\d+)\.\s*Hafta/i);
  return match ? Number(match[1]) : null;
};

const buildWeekBlocks = ({ headerRow, dayRow, periodInfo }) => {
  const blocks = [];
  let currentBlock = null;

  for (let col = 0; col < Math.max(headerRow.length, dayRow.length); col += 1) {
    const headerValue = headerRow[col];
    const weekNumber = getWeekNumberFromTitle(headerValue);

    if (weekNumber) {
      currentBlock = {
        weekNumber,
        title: `${periodInfo.labelUpper} / ${weekNumber}. Hafta`,
        dayColumns: [],
        totalColumnIndex: null
      };
      blocks.push(currentBlock);
    }

    if (!currentBlock) continue;

    const dayValue = dayRow[col];

    if (String(dayValue || "").toLocaleLowerCase("tr-TR").includes("hafta toplam")) {
      currentBlock.totalColumnIndex = col;
      continue;
    }

    const day = Number(dayValue);

    if (Number.isInteger(day) && day >= 1 && day <= periodInfo.daysInMonth) {
      currentBlock.dayColumns.push({ day, sourceColumnIndex: col });
    }
  }

  return blocks.filter((block) => block.dayColumns.length > 0);
};

const getDayOfWeek = ({ year, month, day }) => new Date(year, month - 1, day).getDay();

const isWeekendDay = ({ year, month, day }) => {
  const dayOfWeek = getDayOfWeek({ year, month, day });
  return dayOfWeek === 0 || dayOfWeek === 6;
};

const parseDateParts = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      day: value.getDate(),
      month: value.getMonth() + 1,
      year: value.getFullYear()
    };
  }

  const text = String(value || "").trim();
  const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);

  if (isoMatch) {
    return {
      day: Number(isoMatch[3]),
      month: Number(isoMatch[2]),
      year: Number(isoMatch[1])
    };
  }

  const dateMatch = text.match(/^(\d{1,2})([./-])(\d{1,2})[./-](\d{2,4})/);

  if (!dateMatch) return null;

  const first = Number(dateMatch[1]);
  const separator = dateMatch[2];
  const second = Number(dateMatch[3]);
  let year = Number(dateMatch[4]);

  if (year < 100) {
    year += 2000;
  }

  const looksLikeUsShortDate = separator === "/" && first <= 12 && second >= 1;
  const day = looksLikeUsShortDate ? second : first;
  const month = looksLikeUsShortDate ? first : second;

  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return null;

  return { day, month, year };
};

const getEmploymentBoundsForPeriod = ({ startDate, exitDate, periodInfo }) => {
  const start = parseDateParts(startDate);
  const exit = parseDateParts(exitDate);
  const startsInPeriod = start?.year === periodInfo.year && start?.month === periodInfo.month;
  const exitsInPeriod = exit?.year === periodInfo.year && exit?.month === periodInfo.month;

  return {
    inactiveBeforeDay: startsInPeriod ? start.day : null,
    inactiveAfterDay: exitsInPeriod ? exit.day : null
  };
};

const isInactiveEmploymentDay = ({ day, employmentBounds }) =>
  Boolean(
    (employmentBounds?.inactiveBeforeDay && day < employmentBounds.inactiveBeforeDay) ||
      (employmentBounds?.inactiveAfterDay && day > employmentBounds.inactiveAfterDay)
  );

const isTimeLikeValue = (value) =>
  typeof value === "string" && /^\d{1,3}:\d{2}$/.test(value.trim());

const parseNumberValue = (value) => {
  const normalized = String(value || "")
    .trim()
    .replace(",", ".");
  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
};

const parseHourValueToMinutes = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 60);
  }

  const text = String(value || "").trim();

  if (!text) return 0;

  const hourMinuteMatch = text.match(/^(\d{1,3})[:.,](\d{1,2})$/);

  if (hourMinuteMatch) {
    const hours = Number(hourMinuteMatch[1]);
    const minutes = Number(hourMinuteMatch[2]);

    if (Number.isFinite(hours) && Number.isFinite(minutes) && minutes < 60) {
      return (hours * 60) + minutes;
    }
  }

  const decimalHours = Number(text.replace(",", "."));

  return Number.isFinite(decimalHours) ? Math.round(decimalHours * 60) : 0;
};

const buildDayCode = ({
  value,
  day,
  personName,
  officialHolidayDays,
  bayramHolidayDays,
  specialCodeMap,
  autoCode = ""
}) => {
  const manualCode = specialCodeMap.get(`${normalizeText(personName)}::${day}`);

  if (manualCode) return manualCode;
  if (autoCode) return normalizeCode(autoCode);
  if (bayramHolidayDays.has(day)) return "BT";
  if (officialHolidayDays.has(day)) return "RT";

  const normalizedValue = normalizeCode(value);

  if (REPORT_CODE_DESCRIPTIONS.some(([code]) => normalizeCode(code) === normalizedValue)) {
    return normalizeCode(value);
  }

  return value || "";
};

const isBreakingWeekdayCode = ({ code, year, month, day }) =>
  BREAKING_CODES.has(normalizeCode(code)) && !isWeekendDay({ year, month, day });

const isWorkedLikeCode = (code) => {
  const normalized = normalizeCode(code);

  return WORKED_LIKE_CODES.has(normalized) || isTimeLikeValue(code) || !normalized;
};

const getWeekActualMinutes = (week) => {
  const totalMinutes = parseHourValueToMinutes(week.totalValue);

  if (totalMinutes > 0) return totalMinutes;

  return week.days.reduce(
    (sum, dayItem) => sum + parseHourValueToMinutes(dayItem.sourceValue || dayItem.value),
    0
  );
};

const calculateEmployeeDayTotals = ({
  dayCells,
  periodInfo,
  employmentBounds,
  weeklyWorkingDayValue,
  weeklyWorkingHourValue
}) => {
  let raw5746 = 0;
  let reportDays = 0;
  let oneYearMissingLeaveDays = 0;
  let transfer5510Days = 0;
  const weekGValues = [];
  const weeklyWorkingDays = parseNumberValue(weeklyWorkingDayValue) || 5;
  const weeklyRequiredMinutes = parseHourValueToMinutes(weeklyWorkingHourValue) || 45 * 60;
  const dailyRequiredMinutes = weeklyRequiredMinutes / Math.max(weeklyWorkingDays, 1);
  const hasFullEmploymentInMonth =
    !employmentBounds?.inactiveBeforeDay && !employmentBounds?.inactiveAfterDay;
  const rowHasSensitiveCode = dayCells.some((week) =>
    week.days.some((dayItem) => {
      if (isInactiveEmploymentDay({ day: dayItem.day, employmentBounds })) return false;

      return SENSITIVE_MONTH_CODES.has(normalizeCode(dayItem.value));
    })
  );

  dayCells.forEach((week, weekIndex) => {
    const weekEligibleDays = [];
    let weekRequiredMinutes = 0;
    let weekCreditMinutes = 0;
    const hasWeekBreakingCode = week.days.some((dayItem) => {
      if (isInactiveEmploymentDay({ day: dayItem.day, employmentBounds })) return false;

      return isBreakingWeekdayCode({
        code: dayItem.value,
        year: periodInfo.year,
        month: periodInfo.month,
        day: dayItem.day
      });
    });
    const hasWeekTransferCodeWhenBreakingMonth =
      rowHasSensitiveCode &&
      week.days.some((dayItem) => {
        if (isInactiveEmploymentDay({ day: dayItem.day, employmentBounds })) return false;

        const normalizedCode = normalizeCode(dayItem.value);

        return MONTHLY_5510_TRANSFER_CODES.has(normalizedCode);
      });
    const weekTransfersWeekendTo5510 = hasWeekBreakingCode || hasWeekTransferCodeWhenBreakingMonth;

    week.days.forEach((dayItem) => {
      if (isInactiveEmploymentDay({ day: dayItem.day, employmentBounds })) {
        return;
      }

      const normalizedCode = normalizeCode(dayItem.value);
      const isWeekend = isWeekendDay({
        year: periodInfo.year,
        month: periodInfo.month,
        day: dayItem.day
      });

      if (normalizedCode === "R") {
        reportDays += 1;
        return;
      }

      if (normalizedCode === "İ") {
        oneYearMissingLeaveDays += 1;
        transfer5510Days += 1;
        return;
      }

      if (rowHasSensitiveCode && MONTHLY_5510_TRANSFER_CODES.has(normalizedCode)) {
        transfer5510Days += 1;
        return;
      }

      if (isWeekend && weekTransfersWeekendTo5510) {
        transfer5510Days += 1;
        return;
      }

      if (!isWorkedLikeCode(dayItem.value)) return;

      weekEligibleDays.push(dayItem);

      if (!isWeekend) {
        weekRequiredMinutes += dailyRequiredMinutes;
      }

      if (FULL_DAY_WEEKLY_CREDIT_CODES.has(normalizedCode)) {
        weekCreditMinutes += dailyRequiredMinutes;
      }

      if (
        CONDITIONAL_FULL_DAY_WEEKLY_CREDIT_CODES.has(normalizedCode) &&
        !rowHasSensitiveCode
      ) {
        weekCreditMinutes += dailyRequiredMinutes;
      }

      if (HALF_DAY_WEEKLY_CREDIT_CODES.has(normalizedCode)) {
        weekCreditMinutes += dailyRequiredMinutes / 2;
      }
    });

    const actualMinutes = getWeekActualMinutes(week);
    const creditedMinutes = actualMinutes + weekCreditMinutes;
    const weekG = weekEligibleDays.length === 0
      ? 0
      : creditedMinutes + 1 >= weekRequiredMinutes
        ? weekEligibleDays.length
        : Math.min(
            weekEligibleDays.length,
            Math.floor((creditedMinutes + 1) / Math.max(dailyRequiredMinutes, 1))
          );
    const weekMissing5510Days = Math.max(weekEligibleDays.length - weekG, 0);

    raw5746 += weekG;
    transfer5510Days += weekMissing5510Days;
    weekGValues[weekIndex] = weekG;
  });

  const adjusted5746 =
    periodInfo.daysInMonth === 31 && hasFullEmploymentInMonth ? Math.max(raw5746 - 1, 0) : raw5746;
  const calculated5510 = transfer5510Days;

  return {
    raw5746,
    gun5746: adjusted5746,
    gun5510: calculated5510,
    reportDays,
    oneYearMissingLeaveDays,
    transfer5510Days,
    weekGValues
  };
};

const buildOutputWorkbook = ({
  rows,
  periodInfo,
  projectCode,
  projectName,
  hasReportColumn,
  extraPersonNotes = "",
  activeProjectNotes = []
}) => {
  const aoa = [];
  const baseHeaders = [
    "Sıra No",
    "TCKimlikNo",
    "Personel",
    "Başlangıç Tarihi",
    "Ayrılış Tarihi",
    "Haftalık Çalışma Günü",
    "Haftalık Çalışma Saati",
    "Toplam Çalışma Süresi",
    "İstisnalı Çalışılan Toplam Süre",
    "Tam İstisna İçin Çalışılması Gereken Süre",
    "Hafta Tatili Hakkı Gün",
    "Resmi Tatil Gün",
    "Yıllık İzin Gün",
    "Proje Kodu",
    "Gelir Vergi İstisnası Gün",
    "5746 Gün Sayısı",
    "5510 Gün Sayısı"
  ];

  if (hasReportColumn) {
    baseHeaders.push("Rapor");
  }

  const weekHeaderRow = [...baseHeaders];
  const dayHeaderRow = Array(baseHeaders.length).fill("");

  rows[0]?.weekCells.forEach((week) => {
    const startIndex = weekHeaderRow.length;
    week.days.forEach((dayItem, dayIndex) => {
      weekHeaderRow.push(dayIndex === 0 ? week.title : "");
      dayHeaderRow.push(dayItem.day);
    });
    weekHeaderRow.push("");
    dayHeaderRow.push("Hafta Toplam");
    weekHeaderRow.push("");
    dayHeaderRow.push("G");

    if (weekHeaderRow[startIndex]) {
      // Başlık ilk gün hücresinde tutulur; Excel merge görseli için aralık aşağıda eklenir.
    }
  });

  aoa.push([`${periodInfo.monthNumberLabel} ${periodInfo.labelUpper} MUAFİYETLİ GÜN SAYISI DÖNEM ÇALIŞMA SÜRESİ RAPORU`]);
  aoa.push(weekHeaderRow);
  aoa.push(dayHeaderRow);

  rows.forEach((item, index) => {
    const outputRow = [
      index + 1,
      item.tcKimlikNo,
      item.personel,
      item.baslangicTarihi,
      item.ayrilisTarihi,
      item.haftalikCalismaGunu,
      item.haftalikCalismaSaati,
      item.toplamCalismaSuresi,
      item.istisnaliCalisilanToplamSure,
      item.tamIstisnaIcinGerekenSure,
      item.haftaTatiliHakkiGun,
      item.resmiTatilGun,
      item.yillikIzinGun,
      item.projectCode || projectCode,
      item.totals.gun5746,
      item.totals.gun5746,
      item.totals.gun5510 || ""
    ];

    if (hasReportColumn) {
      outputRow.push(item.totals.reportDays || "");
    }

    item.weekCells.forEach((week) => {
      week.days.forEach((dayItem) => outputRow.push(dayItem.value));
      outputRow.push(week.totalValue);
      outputRow.push(week.gValue);
    });

    aoa.push(outputRow);
  });

  const cleanActiveProjectNotes = Array.isArray(activeProjectNotes)
    ? activeProjectNotes
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const parseFooterPair = (line) => {
    const [firstPart, ...restParts] = String(line || "").split(":");
    const firstValue = String(firstPart || "").trim();
    const secondValue = restParts.join(":").trim();

    return secondValue ? [firstValue, secondValue] : [firstValue, ""];
  };
  const footerHeaderRowIndexes = [];
  const footerDescriptionRowIndexes = [];
  const footerFullRowIndexes = [];

  const pushFooterHeader = (title) => {
    aoa.push([title, "", "", "", "", ""]);
    footerHeaderRowIndexes.push(aoa.length - 1);
  };

  const pushFooterDescription = ([code, description]) => {
    aoa.push([code, description, "", "", "", ""]);
    footerDescriptionRowIndexes.push(aoa.length - 1);
  };

  const pushFooterFull = (line) => {
    aoa.push([line, "", "", "", "", ""]);
    footerFullRowIndexes.push(aoa.length - 1);
  };

  if (cleanActiveProjectNotes.length > 0) {
    aoa.push([]);
    pushFooterHeader("Aktif Projeler");
    cleanActiveProjectNotes.forEach((line) => {
      pushFooterDescription(parseFooterPair(line));
    });
  } else if (projectCode) {
    aoa.push([]);
    pushFooterHeader("Aktif Proje");
    pushFooterDescription([projectCode, projectName || "Proje kodu"]);
  }

  const noteLines = String(extraPersonNotes || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (noteLines.length > 0) {
    aoa.push([]);
    pushFooterHeader("Ek Notlar");
    noteLines.forEach((line) => {
      pushFooterFull(line);
    });
  }

  aoa.push([]);
  pushFooterHeader("Kodlar");
  REPORT_CODE_DESCRIPTIONS.forEach(([code, description]) => {
    pushFooterDescription([code, description]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const workbook = XLSX.utils.book_new();
  const totalColumns = aoa.reduce((max, row) => Math.max(max, row.length), 0);
  const dataHighlightColumnIndexes = Array.from(
    { length: baseHeaders.length - 12 },
    (_, index) => index + 12
  );

  rows.forEach((_, rowIndex) => {
    dataHighlightColumnIndexes.forEach((columnIndex) => {
      const address = XLSX.utils.encode_cell({ r: rowIndex + 3, c: columnIndex });

      if (!worksheet[address]) {
        worksheet[address] = { t: "s", v: "" };
      }
    });
  });
  [...footerHeaderRowIndexes, ...footerDescriptionRowIndexes, ...footerFullRowIndexes].forEach((rowIndex) => {
    for (let columnIndex = 0; columnIndex < 6; columnIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });

      if (!worksheet[address]) {
        worksheet[address] = { t: "s", v: "" };
      }
    }
  });

  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(totalColumns - 1, 0) } }
  ];

  footerHeaderRowIndexes.forEach((rowIndex) => {
    merges.push({
      s: { r: rowIndex, c: 0 },
      e: { r: rowIndex, c: 5 }
    });
  });

  footerDescriptionRowIndexes.forEach((rowIndex) => {
    merges.push({
      s: { r: rowIndex, c: 1 },
      e: { r: rowIndex, c: 5 }
    });
  });

  footerFullRowIndexes.forEach((rowIndex) => {
    merges.push({
      s: { r: rowIndex, c: 0 },
      e: { r: rowIndex, c: 5 }
    });
  });

  baseHeaders.forEach((_, index) => {
    merges.push({
      s: { r: 1, c: index },
      e: { r: 2, c: index }
    });
  });

  let cursor = baseHeaders.length;

  rows[0]?.weekCells.forEach((week) => {
    const span = week.days.length + 2;
    merges.push({
      s: { r: 1, c: cursor },
      e: { r: 1, c: cursor + span - 1 }
    });
    cursor += span;
  });

  worksheet["!merges"] = merges;
  worksheet["!cols"] = Array.from({ length: totalColumns }, (_, index) => {
    if (index >= baseHeaders.length) {
      return { wch: 7 };
    }

    if (index === 0) {
      return { wch: 8 };
    }

    if (index === 1 || index === 3 || index === 4) {
      return { wch: 15 };
    }

    if (index === 2) {
      return { wch: 24 };
    }

    return { wch: 10 };
  });
  worksheet["!rows"] = aoa.map((_, index) => ({
    hpt: index === 0 ? 50 : index < 3 ? 34 : 21
  }));

  XLSX.utils.book_append_sheet(workbook, worksheet, "Page1");

  return workbook;
};

export const extractExemptionEmployeesFromWorkbook = (workbook) => {
  const firstSheetName = workbook?.SheetNames?.[0];
  const worksheet = firstSheetName ? workbook.Sheets[firstSheetName] : null;

  if (!worksheet) return [];

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false
  });
  const headerRowIndex = findHeaderRowIndex(rows);

  if (headerRowIndex === -1) return [];

  const headerRow = rows[headerRowIndex] || [];
  const columns = {
    tcKimlikNo: findColumnIndex(headerRow, ["TCKimlikNo"]),
    personel: findColumnIndex(headerRow, ["Personel"])
  };
  const employees = [];
  const seen = new Set();

  for (let rowIndex = headerRowIndex + 2; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    const tcKimlikNo = getCell(row, columns.tcKimlikNo);
    const personel = getCell(row, columns.personel);
    const key = getExemptionEmployeeKey(personel);

    if (!personel || !key || seen.has(key)) continue;

    seen.add(key);
    employees.push({
      key,
      tcKimlikNo,
      personel
    });
  }

  return employees.sort((a, b) =>
    String(a.personel || "").localeCompare(String(b.personel || ""), "tr", { sensitivity: "base" })
  );
};

export const generateExemptionDayReportFromWorkbook = ({
  workbook,
  periodKey,
  projectCode,
  projectName,
  officialHolidayDays = [],
  bayramHolidayDays = [],
  specialCodeText = "",
  extraPersonNotes = "",
  includedEmployeeKeys = null,
  assignmentProjectCodes = null,
  activeProjectNotes = []
}) => {
  const periodInfo = getPeriodInfo(periodKey);
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];

  if (!worksheet) {
    throw new Error("Excel içinde okunacak sayfa bulunamadı.");
  }

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false
  });
  const headerRowIndex = findHeaderRowIndex(rows);

  if (headerRowIndex === -1) {
    throw new Error("TCKimlikNo ve Personel başlıkları bulunan satır bulunamadı.");
  }

  const headerRow = rows[headerRowIndex] || [];
  const dayRow = rows[headerRowIndex + 1] || [];
  const dataStartRowIndex = headerRowIndex + 2;
  const officialHolidaySet = new Set(officialHolidayDays);
  const bayramHolidaySet = new Set(bayramHolidayDays);
  const specialCodes = parseSpecialCodeLines(specialCodeText);
  const weekBlocks = buildWeekBlocks({ headerRow, dayRow, periodInfo });

  if (weekBlocks.length === 0) {
    throw new Error("Hafta/gün kolonları bulunamadı.");
  }

  const columns = {
    tcKimlikNo: findColumnIndex(headerRow, ["TCKimlikNo"]),
    personel: findColumnIndex(headerRow, ["Personel"]),
    baslangicTarihi: findColumnIndex(headerRow, ["Başlangıç Tarihi"]),
    ayrilisTarihi: findColumnIndex(headerRow, ["Ayrılış Tarihi"]),
    haftalikCalismaGunu: findColumnIndex(headerRow, ["Haftalık Çalışma Günü"]),
    haftalikCalismaSaati: findColumnIndex(headerRow, ["Haftalık Çalışma Saati"]),
    toplamCalismaSuresi: findColumnIndex(headerRow, ["Toplam Çalışma Süresi"]),
    istisnaliCalisilanToplamSure: findColumnIndex(headerRow, ["İstisnalı Çalışılan Toplam Süre"]),
    tamIstisnaIcinGerekenSure: findColumnIndex(headerRow, ["Tam İstisna İçin Çalışılması Gereken Süre"]),
    haftaTatiliHakkiGun: findColumnIndex(headerRow, ["Hafta Tatili Hakkı Gün"]),
    resmiTatilGun: findColumnIndex(headerRow, ["Resmi Tatil Gün"]),
    yillikIzinGun: findColumnIndex(headerRow, ["Yıllık İzin Gün"])
  };

  const employeeRows = [];

  for (let rowIndex = dataStartRowIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    const tcKimlikNo = getCell(row, columns.tcKimlikNo);
    const personel = getCell(row, columns.personel);

    if (!personel) continue;

    const employmentBounds = getEmploymentBoundsForPeriod({
      startDate: getCell(row, columns.baslangicTarihi),
      exitDate: getCell(row, columns.ayrilisTarihi),
      periodInfo
    });
    const weekCells = weekBlocks.map((week) => {
      const days = week.dayColumns.map((dayColumn) => {
        const cellValue = getCell(row, dayColumn.sourceColumnIndex);
        const sourceCell = getWorksheetCell(worksheet, rowIndex, dayColumn.sourceColumnIndex);
        const hasAnnualLeaveFill = isAnnualLeaveGreyFill(sourceCell);
        const hasWorkedHalfDayHours = hasAnnualLeaveFill && parseHourValueToMinutes(cellValue) > 0;
        const autoCode = hasWorkedHalfDayHours
          ? "YG"
          : !String(cellValue || "").trim() && hasAnnualLeaveFill
            ? "Yİ"
            : "";

        return {
          day: dayColumn.day,
          sourceValue: cellValue,
          value: buildDayCode({
            value: cellValue,
            day: dayColumn.day,
            personName: personel,
            officialHolidayDays: officialHolidaySet,
            bayramHolidayDays: bayramHolidaySet,
            specialCodeMap: specialCodes.map,
            autoCode
          })
        };
      });

      return {
        title: week.title,
        days,
        totalValue: getCell(row, week.totalColumnIndex),
        gValue: 0
      };
    });
    const weeklyWorkingDayValue = getCell(row, columns.haftalikCalismaGunu);
    const weeklyWorkingHourValue = getCell(row, columns.haftalikCalismaSaati);
    const totals = calculateEmployeeDayTotals({
      dayCells: weekCells,
      periodInfo,
      employmentBounds,
      weeklyWorkingDayValue,
      weeklyWorkingHourValue
    });
    const weekCellsWithTotals = weekCells.map((week, index) => ({
      ...week,
      gValue: totals.weekGValues[index] ?? 0
    }));

    const employeeKey = getExemptionEmployeeKey(personel);

    employeeRows.push({
      tcKimlikNo,
      personel,
      projectCode: assignmentProjectCodes?.[employeeKey] || projectCode || "",
      baslangicTarihi: getCell(row, columns.baslangicTarihi),
      ayrilisTarihi: getCell(row, columns.ayrilisTarihi),
      haftalikCalismaGunu: weeklyWorkingDayValue,
      haftalikCalismaSaati: weeklyWorkingHourValue,
      toplamCalismaSuresi: getCell(row, columns.toplamCalismaSuresi),
      istisnaliCalisilanToplamSure: getCell(row, columns.istisnaliCalisilanToplamSure),
      tamIstisnaIcinGerekenSure: getCell(row, columns.tamIstisnaIcinGerekenSure),
      haftaTatiliHakkiGun: getCell(row, columns.haftaTatiliHakkiGun),
      resmiTatilGun: getCell(row, columns.resmiTatilGun),
      yillikIzinGun: getCell(row, columns.yillikIzinGun),
      weekCells: weekCellsWithTotals,
      totals,
      employmentBounds
    });
  }

  if (employeeRows.length === 0) {
    throw new Error("Personel satırı bulunamadı.");
  }

  const includedKeySet = Array.isArray(includedEmployeeKeys)
    ? new Set(includedEmployeeKeys)
    : includedEmployeeKeys instanceof Set
      ? includedEmployeeKeys
      : null;
  const filteredEmployeeRows = includedKeySet
    ? employeeRows.filter((row) => includedKeySet.has(getExemptionEmployeeKey(row.personel)))
    : employeeRows;

  if (filteredEmployeeRows.length === 0) {
    throw new Error("Rapor için atanmış personel bulunamadı.");
  }

  if (filteredEmployeeRows !== employeeRows) {
    employeeRows.length = 0;
    employeeRows.push(...filteredEmployeeRows);
  }

  employeeRows.sort((a, b) =>
    String(a.personel || "").localeCompare(String(b.personel || ""), "tr", { sensitivity: "base" })
  );

  const hasReportColumn = employeeRows.some((row) => row.totals.reportDays > 0);
  const outputWorkbook = buildOutputWorkbook({
    rows: employeeRows,
    periodInfo,
    projectCode,
    projectName,
    hasReportColumn,
    extraPersonNotes,
    activeProjectNotes
  });

  return {
    workbook: outputWorkbook,
    rows: employeeRows,
    warnings: specialCodes.warnings,
    periodInfo,
    hasReportColumn,
    projectCode,
    projectName,
    officialHolidayDays,
    bayramHolidayDays,
    extraPersonNotes,
    activeProjectNotes,
    fileName: `${periodInfo.monthNumberLabel} ${periodInfo.label} Muafiyetli Gün Sayısı Raporu.xlsx`
  };
};

const numberToColumnName = (number) => {
  let value = number;
  let name = "";

  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }

  return name;
};

const columnNameToNumber = (name) =>
  String(name || "")
    .split("")
    .reduce((sum, char) => sum * 26 + char.charCodeAt(0) - 64, 0);

const EXEMPTION_STYLE_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="6">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="18"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="10"/><color rgb="FF1F2937"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="10"/><color rgb="FFC00000"/><name val="Calibri"/><family val="2"/></font>
    <font><sz val="10"/><color rgb="FFC00000"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="9">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF6495ED"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFCFE1FE"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFCD5C5C"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF5F5F5"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEAF2FF"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE5E7EB"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1F4E78"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FF9CA3AF"/></left>
      <right style="thin"><color rgb="FF9CA3AF"/></right>
      <top style="thin"><color rgb="FF9CA3AF"/></top>
      <bottom style="thin"><color rgb="FF9CA3AF"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="15">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="2" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="5" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="3" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="4" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="7" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="5" fillId="8" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="2" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
  <dxfs count="0"/>
  <tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleLight16"/>
</styleSheet>`;

const setCellStyleInSheetXml = (sheetXml, styleResolver) =>
  sheetXml.replace(/<c r="([A-Z]+)(\d+)"([^>]*)>/g, (match, column, rowText, attributes) => {
    const row = Number(rowText);
    const col = columnNameToNumber(column);
    const styleId = styleResolver({ row, col, column });

    if (!styleId) return match;

    const cleanAttributes = attributes.replace(/\s+s="[^"]*"/, "");
    return `<c r="${column}${row}" s="${styleId}"${cleanAttributes}>`;
  });

export const downloadStyledExemptionDayReport = async (report) => {
  if (!report?.workbook) {
    throw new Error("İndirilecek rapor bulunamadı.");
  }

  const workbookArray = XLSX.write(report.workbook, {
    bookType: "xlsx",
    type: "array"
  });
  const zip = await JSZip.loadAsync(workbookArray);
  const sheetPath = "xl/worksheets/sheet1.xml";
  const sheetFile = zip.file(sheetPath);

  if (!sheetFile) {
    throw new Error("Excel sayfası hazırlanamadı.");
  }

  const sheetXml = await sheetFile.async("string");
  const worksheet = report.workbook.Sheets.Page1;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  const totalColumns = range.e.c + 1;
  const dataStartRow = 4;
  const dataEndRow = dataStartRow + report.rows.length - 1;
  const activeProjectNotes = Array.isArray(report.activeProjectNotes)
    ? report.activeProjectNotes.filter(Boolean)
    : [];
  let footerCursor = dataEndRow;
  const footerHeaderRows = new Set();
  const footerBodyRows = new Set();

  if (activeProjectNotes.length > 0) {
    footerCursor += 2;
    footerHeaderRows.add(footerCursor);
    activeProjectNotes.forEach(() => {
      footerCursor += 1;
      footerBodyRows.add(footerCursor);
    });
  } else if (report.projectCode) {
    footerCursor += 2;
    footerHeaderRows.add(footerCursor);
    footerCursor += 1;
    footerBodyRows.add(footerCursor);
  }

  const extraNoteLines = String(report.extraPersonNotes || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (extraNoteLines.length > 0) {
    footerCursor += 2;
    footerHeaderRows.add(footerCursor);
    extraNoteLines.forEach(() => {
      footerCursor += 1;
      footerBodyRows.add(footerCursor);
    });
  }

  footerCursor += 2;
  footerHeaderRows.add(footerCursor);
  REPORT_CODE_DESCRIPTIONS.forEach(() => {
    footerCursor += 1;
    footerBodyRows.add(footerCursor);
  });

  const baseColumnCount = report.hasReportColumn ? 18 : 17;
  const officialHolidaySet = new Set(report.officialHolidayDays || []);
  const bayramHolidaySet = new Set(report.bayramHolidayDays || []);

  const weekGColumns = new Set();
  const weekTotalColumns = new Set();
  const weekDayColumns = new Map();
  let cursor = baseColumnCount + 1;

  report.rows[0]?.weekCells.forEach((week) => {
    week.days.forEach((dayItem) => {
      weekDayColumns.set(cursor, dayItem.day);
      cursor += 1;
    });
    weekTotalColumns.add(cursor);
    cursor += 1;
    weekGColumns.add(cursor);
    cursor += 1;
  });

  const styledXml = setCellStyleInSheetXml(sheetXml, ({ row, col }) => {
    if (row === 1) return 1;
    if (footerHeaderRows.has(row) && col <= 6) return 13;
    if (footerBodyRows.has(row) && col <= 6) return 14;

    if (row === 2) {
      if (weekGColumns.has(col)) return 4;
      if (col >= 13 && col <= baseColumnCount) return 13;
      if (col <= baseColumnCount) return 10;
      return col <= totalColumns ? 2 : 0;
    }

    if (row === 3) {
      const cellAddress = `${numberToColumnName(col)}${row}`;
      const cellValue = worksheet[cellAddress]?.v;
      const day = Number(cellValue);

      if (weekGColumns.has(col)) return 4;
      if (col <= baseColumnCount) return 10;
      if (officialHolidaySet.has(day) || bayramHolidaySet.has(day)) return 3;
      return col <= totalColumns ? 2 : 0;
    }

    if (row >= dataStartRow && row <= dataEndRow) {
      const day = weekDayColumns.get(col);
      const reportRow = report.rows[row - dataStartRow];
      const inactiveBeforeDay = reportRow?.employmentBounds?.inactiveBeforeDay;
      const inactiveAfterDay = reportRow?.employmentBounds?.inactiveAfterDay;

      if (
        day &&
        ((inactiveBeforeDay && day < inactiveBeforeDay) ||
          (inactiveAfterDay && day > inactiveAfterDay))
      ) {
        return 11;
      }

      if (col === 3) return 6;
      if (col >= 13 && col <= baseColumnCount) return 12;
      if (weekGColumns.has(col)) return 12;
      if (weekTotalColumns.has(col)) return 7;
      return 5;
    }

    return 0;
  });

  zip.file(sheetPath, styledXml);
  zip.file("xl/styles.xml", EXEMPTION_STYLE_XML);

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = report.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
